import * as React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';

import {type SpringIconType} from '../../icons/Icon';
import {useSize, type SizeContextSizeValue} from '../../themes';
import {getDefinedValue, renderElement} from '../../utils';
import {Spinner} from '../Spinner';
import type {LinkTargets} from '../Link';
import type {HTMLAttributes, AsProps, SpringTooling} from '../__SharedTypes';
import type {StackProps} from '../Stack';

import {
  ButtonView,
  SpaceSaver,
  LoadingWrapper,
  ButtonContentView,
  styleTokens,
} from './Button.styles';
import {useButtonGroupContext} from './ButtonGroupContext';
import {useTruncation} from '../../hooks';
import type {TruncationProps} from '../../hooks';

export type ButtonPrefixSuffix =
  | SpringIconType
  | React.ReactElement<React.ComponentProps<SpringIconType>>
  | React.ReactElement<React.ComponentProps<typeof Spinner>>
  | null;

export type ButtonColorVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  // Color variants
  | 'black'
  | 'blue'
  | 'green'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'white';

/**
 * @types ButtonProps
 *
 * When `isBranded` is true, only specific colorVariant + variant combinations are officially supported:
 * - solid (default variant): colorVariant 'default' | 'blue'
 * - ghost: colorVariant 'default' | 'blue'
 * - outline: colorVariant 'default' | 'white'
 *
 * Both density values ('comfortable' | 'compact') work with all branded combinations.
 */
export type ButtonProps = {
  /**
   * The busy state for screen readers.
   */
  ['aria-busy']?: boolean;
  /**
   * The contents of the button.
   */
  children?: React.ReactNode;
  /**
   * The color of the button.
   */
  colorVariant?: ButtonColorVariant;
  /**
   * The URL target for the Button as a link.
   */
  href?: string;
  /**
   * Activates branded styling (WF Visual Sans, fixed sizing that ignores SizeContext).
   * Only specific colorVariant + variant combinations are supported.
   */
  isBranded?: boolean;
  /**
   * Whether the button is disabled or not.
   */
  isDisabled?: boolean;
  /**
   * Sets the button to the full width of its parent container.
   */
  isFullWidth?: boolean;
  /**
   * Sets the loading state for the button.
   */
  isLoading?: boolean;
  /**
   * Sets the selected state for the button. This is used for toggle buttons (typically in a ButtonGroup).
   */
  isSelected?: boolean;
  /**
   * The justification of the button's content.
   * Defaults to `center`.
   */
  justify?: StackProps['justify'];
  /**
   * Icon to show before the Button's text.
   */
  prefix?: ButtonPrefixSuffix;
  /**
   * Change button size to override size context.
   *
   * @deprecated
   */
  size?: SizeContextSizeValue;
  /**
   * Icon to show after the Button's text.
   */
  suffix?: ButtonPrefixSuffix;
  /**
   * Expand prefix and suffix to edge of button, but maintain text in center.
   * This prop is used internally for `MenuTrigger`.
   */
  shouldEdgeAlignIcons?: boolean;
  /**
   * A string that indicates where to display the linked resource.
   * `_self` opens the link in the same frame as it was clicked (this is default).
   * `_blank` opens the link in a new window or tab.
   */
  target?: LinkTargets;
  /**
   * Props for truncating text within the button.
   */
  truncationProps?: Omit<TruncationProps, 'truncateToLines'> & {
    /**
     * Values higher than 1 cause clipping issues, since the internal HStack for Button text is not limited by width and therefore no wrapping can occur.
     * None of our buttons currently support multi-line text, so this should not be a problem.
     */
    truncateToLines?: 1;
  };
  /**
   * The button's behavior type.
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * The variant of the button.
   */
  variant?: 'solid' | 'subtle' | 'outline' | 'ghost';
  /**
   * The density of the button.
   */
  density?: 'comfortable' | 'compact';
} & AsProps &
  Omit<HTMLAttributes, 'prefix'> &
  SpringTooling;

/**
 * We are memoizing this section because button props (e.g. onClick & colorVariant) are more likely to change than these props.
 *
 * Memoizing this section could prevent emotionStyled from re-running up to 6 or so times.
 * This is well worth the perf trade-off for memoizing, IMHO.
 */
type ButtonContentProps = {
  children?: React.ReactNode;
  isLoading?: boolean;
  justify?: StackProps['justify'];
  prefix?: ButtonPrefixSuffix;
  suffix?: ButtonPrefixSuffix;
  shouldEdgeAlignIcons?: boolean;
  shouldTruncate: boolean;
  truncationProps?: TruncationProps;
};

const ButtonContent = React.memo(
  ({
    isLoading,
    justify,
    prefix,
    children,
    suffix,
    shouldEdgeAlignIcons,
    shouldTruncate,
    truncationProps,
  }: ButtonContentProps) => {
    const {truncationStyles, content} = useTruncation({
      ...truncationProps,
      children,
    });

    return (
      <>
        {isLoading ? (
          <LoadingWrapper>
            <Spinner size={16} />
          </LoadingWrapper>
        ) : null}
        {/* Used to visually hide the text, but maintain the button's width */}
        <SpaceSaver hide={isLoading} justify={justify}>
          {prefix ? renderElement(prefix) : null}
          {content ? (
            <ButtonContentView
              justify={justify}
              shouldEdgeAlignIcons={shouldEdgeAlignIcons}
              shouldTruncate={shouldTruncate}
            >
              {shouldTruncate ? (
                <span style={truncationStyles}>{content}</span>
              ) : (
                content
              )}
            </ButtonContentView>
          ) : null}
          {suffix ? renderElement(suffix) : null}
        </SpaceSaver>
      </>
    );
  }
);

ButtonContent.displayName = 'ButtonContent';

/**
 * # Button
 *
 * Buttons allow users to take actions, such as saving changes, accepting payments, and creating folders.
 *
 * ## Usage
 *
 * ```jsx
 * import * as React from "react";
 * import { Button } from "@packages/systems/spring";
 *
 * export const Example = () => {
 *   return (
 *    <>
 *      <Button>Default Button</Button>
 *      <Button colorVariant='primary' variant='solid'>Primary Button</Button>
 *      <Button colorVariant='success' variant='ghost'>Success Button</Button>
 *      <Button colorVariant='danger' variant='outline'>Danger Button</Button>
 *      <Button colorVariant='ghost'>Ghost Button</Button>
 *    </>
 * )};
 * ```
 *
 * @component
 */
const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as: asProp,
      children,
      colorVariant,
      href,
      isBranded,
      prefix,
      isDisabled,
      isFullWidth,
      isLoading,
      isSelected,
      justify, // @SPRING_CLEANING Setting a default value here is causing Button's propTable from rendering
      onClick,
      shouldEdgeAlignIcons,
      size: incomingSize,
      suffix,
      truncationProps = emptyObject,
      type = 'button',
      variant: variantProp,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const {
      colorVariant: buttonGroupColorVariant,
      truncationProps: buttonGroupTruncationProps,
      variant: buttonGroupVariant,
    } = useButtonGroupContext();

    const colorVariantValue =
      colorVariant || buttonGroupColorVariant || 'default';
    const variantValue = variantProp || buttonGroupVariant;

    // First check our individual Button components for truncation props,
    // and then check the ButtonGroupContext for truncation props.
    const truncationPropsValue =
      truncationProps && Object.keys(truncationProps).length
        ? truncationProps
        : buttonGroupTruncationProps &&
            Object.keys(buttonGroupTruncationProps).length
          ? buttonGroupTruncationProps
          : emptyObject;

    // Because we're potentially getting our truncation props from the ButtonGroupContext,
    // we only want to apply the truncation props to Button components and not IconButton components.
    // So, we need to check for both the presence of the truncation props and that it's not an IconButton
    // in order to correctly apply the truncation behavior.
    const shouldTruncate = Boolean(
      Object.keys(truncationPropsValue).length &&
      props['data-sc'] !== 'IconButton'
    );

    const size = useSize(incomingSize);
    const isButtonDisabled = isDisabled || isLoading;
    const isLink = Boolean(href);
    const asBaseElement = isLink ? 'a' : 'button';
    const as = getDefinedValue(asProp, asBaseElement);

    const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
      /**
       * Improves a11y for click interactions when the Button is disabled.
       *
       * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-disabled
       */
      if (isButtonDisabled) {
        event?.preventDefault?.();
        return;
      }
      onClick && onClick(event);
    };

    return (
      <ButtonView
        aria-busy={isLoading}
        aria-disabled={isButtonDisabled}
        as={as}
        colorVariant={colorVariantValue}
        isBranded={isBranded}
        {...props}
        // @ts-expect-error - @SPRING_CLEANING: Handle href better on this Button that can be a button or link.
        href={href || null}
        isFullWidth={isFullWidth}
        isLoading={isLoading}
        isPressed={isSelected}
        isSelected={isSelected}
        onClick={handleOnClick}
        ref={ref}
        shouldTruncate={shouldTruncate}
        size={size}
        type={type}
        variant={variantValue}
      >
        <ButtonContent
          isLoading={isLoading}
          justify={justify ?? 'center'}
          prefix={prefix}
          shouldEdgeAlignIcons={shouldEdgeAlignIcons}
          shouldTruncate={shouldTruncate}
          suffix={suffix}
          truncationProps={{...(shouldTruncate && truncationPropsValue)}}
        >
          {children}
        </ButtonContent>
      </ButtonView>
    );
  }
);

ButtonComponent.displayName = 'Button';
ButtonComponent.toString = ButtonView.toString;

export const Button = Object.assign({}, ButtonComponent, {
  __experimentalTokens: styleTokens,
});

export default Button;
